import { useState, useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { IoMdArrowBack } from "react-icons/io";
import { FiSearch, FiChevronRight } from "react-icons/fi";
import { MdLocationOn, MdPhone } from "react-icons/md";
import { getHospitalList } from "../../../../../Service/RM";
import Loader from "../../../../../reusableUI/loader";
import { showToast } from "../../../../../reusableUI/toaster";
import { capitalizeFirstLetter, formatPhoneNumber } from "../../../../../utils/Functions";
import { CommonImages } from "../../../../../reusableUI/images";

const EmptyState = () => (
  <div className="flex flex-col items-center justify-center py-16">
    <img
      src={CommonImages.noDuty}
      alt=""
      className="h-[120px] w-[150px] object-contain opacity-70"
    />
    <p className="font-poppins text-[14px] font-semibold text-[#787998] mt-2">
      No partners found
    </p>
    <p className="font-poppins text-[12px] text-gray-400 mt-1">
      Try a different search.
    </p>
  </div>
);

const PartnerCard = ({ partner, onOpen }) => {
  const rawPhone = partner?.contactInfo?.[0]?.phone;

  return (
    <button
      type="button"
      onClick={onOpen}
      className="w-full text-left bg-white rounded-[14px] border border-[#E5E7EB] shadow-sm p-3.5 flex items-center gap-3 active:bg-gray-50 transition-colors"
    >
      <img
        src={CommonImages.menwithoutactive}
        alt=""
        className="h-11 w-11 rounded-full object-cover shrink-0"
      />

      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2">
          <p className="font-poppins font-semibold text-[15px] text-[#1F2937] truncate">
            {capitalizeFirstLetter(partner?.basicInfo?.name) || "Unknown"}
          </p>
          {partner?.providerType && (
            <span className="shrink-0 text-[10px] font-poppins font-medium text-[#1D4ED8] bg-[#EEF2FF] px-2 py-0.5 rounded-full capitalize">
              {partner.providerType}
            </span>
          )}
        </div>

        <div className="flex items-center gap-3 mt-1 text-[12px] font-poppins text-gray-500">
          {partner?.addressInfo?.city && (
            <span className="flex items-center gap-1 min-w-0">
              <MdLocationOn size={13} className="shrink-0" />
              <span className="truncate">{capitalizeFirstLetter(partner.addressInfo.city)}</span>
            </span>
          )}
          {rawPhone && (
            <span className="flex items-center gap-1 min-w-0">
              <MdPhone size={13} className="shrink-0" />
              <span className="truncate">{formatPhoneNumber(Number(rawPhone))}</span>
            </span>
          )}
        </div>
      </div>

      <FiChevronRight size={20} className="text-gray-300 shrink-0" />
    </button>
  );
};

const PartnerListMobile = ({ searchTerm }) => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [partners, setPartners] = useState([]);
  const [search, setSearch] = useState("");

  useEffect(() => {
    let cancelled = false;

    const fetchPartners = async () => {
      setLoading(true);
      try {
        const result = await getHospitalList();
        if (cancelled) return;

        if (!result?.error && Array.isArray(result?.data)) {
          setPartners(result.data);
        } else {
          setPartners([]);
        }
      } catch (err) {
        console.error("Error fetching partner list:", err);
        if (!cancelled) {
          setPartners([]);
          showToast("Failed to load partners", "Error");
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    fetchPartners();
    return () => {
      cancelled = true;
    };
  }, []);

  const filtered = useMemo(() => {
    const list = Array.isArray(partners) ? [...partners] : [];
    const local = search.trim().toLowerCase();
    const global = String(searchTerm || "").trim().toLowerCase();

    const matches = (p) => {
      const name = p?.basicInfo?.name?.toLowerCase() || "";
      const phone = String(p?.contactInfo?.[0]?.phone ?? "");
      const city = p?.addressInfo?.city?.toLowerCase() || "";
      const matchLocal =
        !local || name.includes(local) || phone.includes(local) || city.includes(local);
      const matchGlobal =
        !global || name.includes(global) || phone.includes(global) || city.includes(global);
      return matchLocal && matchGlobal;
    };

    return list.filter(matches).reverse();
  }, [partners, search, searchTerm]);

  const openPartner = (partner) => {
    localStorage.setItem("profilePage", "partnerMobile");
    navigate(
      `/admin/partnerMobile?partnerId=${encodeURIComponent(partner._id)}&tab=careTeam`,
      { state: { userID: partner._id } }
    );
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center min-h-[70vh]">
        <Loader loading={loading} />
      </div>
    );
  }

  return (
    <div className="h-[100dvh] bg-[#F5F7FB] overflow-hidden">
      <div className="h-full overflow-y-auto overscroll-contain">
      {/* Sticky header + search */}
      <div className="sticky top-0 z-20 bg-[#F5F7FB] px-4 pt-3 pb-2">
        <div className="flex items-center gap-2 mb-3">
          <button
            type="button"
            onClick={() => navigate("/admin/dashboard")}
            className="p-2 -ml-2 rounded-full hover:bg-gray-200 transition-colors"
            aria-label="Back"
          >
            <IoMdArrowBack size={22} color="#111827" />
          </button>
          <h2 className="text-[18px] font-poppins font-semibold text-[#1F2937]">
            Partners ({filtered.length})
          </h2>
        </div>

        <div className="relative">
          <FiSearch
            size={18}
            className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none"
          />
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search by name, phone or city"
            className="w-full pl-10 pr-3 py-2.5 rounded-xl border border-gray-200 bg-white text-[14px] font-poppins focus:outline-none focus:border-[#1D4ED8]"
          />
        </div>
      </div>

      {/* List */}
      <div className="px-4 pb-8 space-y-3">
        {filtered.length === 0 ? (
          <EmptyState />
        ) : (
          filtered.map((partner) => (
            <PartnerCard
              key={partner._id}
              partner={partner}
              onOpen={() => openPartner(partner)}
            />
          ))
        )}
      </div>
      </div>
    </div>
  );
};

export default PartnerListMobile;
